import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Camera, Check, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND}/api`;

/**
 * Fast Capture v1 — minimal getUserMedia-based rapid burst capture.
 *
 * Goal: eliminate the OS "Käytä kuvaa / Ota uudelleen" round-trip and the
 * camera HW restart between photos. Live preview stays open while the user
 * keeps tapping the shutter — each tap is a sub-second capture.
 *
 * Intentionally NOT included (per user spec for v1):
 *   - No filters, no zoom, no flash toggle, no front/back swap, no settings
 *   - No HDR, no slow-mo, no video — just photos
 *
 * Fallback: if getUserMedia fails (permission denied, no camera, very old
 * browser), the parent should open MultiCaptureSheet source="camera" instead.
 * We surface that via the `onFallback` prop.
 */
export default function FastCaptureSheet({ report, unit, editToken, onClose, onCompleted, onFallback }) {
  const isEditMode = !!editToken;
  const photoUrl = isEditMode
    ? `${API_BASE}/reports/edit/${editToken}/units/${unit.id}/photos`
    : `/reports/${report.report_id}/units/${unit.id}/photos`;
  const post = (url, body, config) => (isEditMode ? axios.post(url, body, config) : api.post(url, body, config));

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const itemIdRef = useRef(0);

  const [phase, setPhase] = useState("starting"); // starting | live | uploading | denied | error
  const [items, setItems] = useState([]);
  const [uploaded, setUploaded] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [flashing, setFlashing] = useState(false);

  // Acquire camera stream on mount
  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("Selain ei tue live-kameraa");
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1440 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          // iOS Safari requires explicit play() after srcObject assignment
          try { await videoRef.current.play(); } catch { /* swallow autoplay races */ }
        }
        setPhase("live");
      } catch (err) {
        if (cancelled) return;
        const name = err?.name || "";
        if (name === "NotAllowedError" || name === "PermissionDeniedError") {
          setPhase("denied");
          setErrorMsg("Kameran käyttöoikeus evätty. Salli kamera selaimen asetuksista tai käytä tiedostonvalitsinta.");
        } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
          setPhase("error");
          setErrorMsg("Kameraa ei löytynyt tästä laitteesta.");
        } else {
          setPhase("error");
          setErrorMsg(err?.message || "Kameran avaaminen epäonnistui");
        }
      }
    };
    start();
    return () => {
      cancelled = true;
      const s = streamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  // Revoke object URLs on unmount
  useEffect(() => {
    return () => items.forEach((it) => it.previewUrl && URL.revokeObjectURL(it.previewUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shoot = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    // Brief white flash for tactile feedback
    setFlashing(true);
    setTimeout(() => setFlashing(false), 90);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.92),
      );
      if (!blob) return;
      const id = ++itemIdRef.current;
      const file = new File([blob], `fast-${id}.jpg`, { type: "image/jpeg" });
      const previewUrl = URL.createObjectURL(file);
      const compressPromise = compressImage(file).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("fast-capture compress failed:", err);
        return null;
      });
      setItems((prev) => [...prev, { id, file, previewUrl, compressPromise, status: "pending", error: null }]);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("fast capture shoot error:", err);
    }
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((it) => {
      if (it.id === id && it.previewUrl) URL.revokeObjectURL(it.previewUrl);
      return it.id !== id;
    }));
  };

  const uploadAll = async () => {
    if (items.length === 0) return;
    setPhase("uploading");
    setUploaded(0);
    const updated = [...items];
    let okCount = 0;
    let errCount = 0;
    let lastReport = null;
    for (let i = 0; i < updated.length; i++) {
      const it = updated[i];
      try {
        let compressed = it.compressPromise ? await it.compressPromise : null;
        if (!compressed) compressed = await compressImage(it.file);
        const fd = new FormData();
        fd.append("file", compressed, it.file.name);
        fd.append("caption", "");
        fd.append("is_finding", "false");
        const { data } = await post(photoUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
        updated[i] = { ...it, status: "done" };
        okCount++;
        setUploaded((n) => n + 1);
        if (data?.report) lastReport = data.report;
      } catch (err) {
        updated[i] = { ...it, status: "error", error: err?.response?.data?.detail || err?.message || "Lataus epäonnistui" };
        errCount++;
      }
      setItems([...updated]);
    }
    if (errCount === 0) {
      // Ei success-toastia — kenttätyöskentelyssä tarpeeton, jää häiritsemään.
      if (lastReport) onCompleted?.(lastReport, okCount);
      setTimeout(() => onClose(), 250);
    } else if (okCount > 0) {
      if (lastReport) onCompleted?.(lastReport, okCount);
      toast.warning(`${errCount} kuvaa epäonnistui — yritä uudelleen`);
      setPhase("live");
      setItems(updated.filter((it) => it.status !== "done"));
    } else {
      toast.error("Lataus epäonnistui — yritä uudelleen");
      setPhase("live");
    }
  };

  // PERMISSION DENIED / ERROR view
  if (phase === "denied" || phase === "error") {
    return (
      <div className="fixed inset-0 z-[60] bg-black/70 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="fast-capture-sheet">
        <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-600">Live-kamera</div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="fast-capture-close">
              <X className="h-5 w-5 text-slate-400" />
            </button>
          </div>
          <div className="p-6 text-center" data-testid="fast-capture-error">
            <div className="mx-auto h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center mb-3">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <div className="font-display font-semibold text-slate-900">{phase === "denied" ? "Kameran käyttöoikeus evätty" : "Kameraa ei voitu avata"}</div>
            <div className="text-sm text-slate-600 mt-2">{errorMsg}</div>
            <button
              type="button"
              onClick={() => { onClose(); onFallback?.(); }}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm"
              data-testid="fast-capture-fallback"
            >
              Käytä tiedostonvalitsinta
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col" data-testid="fast-capture-sheet">
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between p-3 bg-gradient-to-b from-black/70 to-transparent">
        <div className="min-w-0 pr-3">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-300">Live-kamera</div>
          <div className="font-display text-base font-semibold text-white truncate">{unit.label}</div>
        </div>
        <button
          onClick={onClose}
          disabled={phase === "uploading"}
          className="p-2 rounded-full bg-black/50 backdrop-blur hover:bg-black/70 disabled:opacity-40"
          data-testid="fast-capture-close"
        >
          <X className="h-5 w-5 text-white" />
        </button>
      </div>

      {/* Video preview */}
      <div className="relative flex-1 overflow-hidden bg-black">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="fast-capture-video"
        />
        {/* Loader while starting */}
        {phase === "starting" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/80" data-testid="fast-capture-starting">
            <Loader2 className="h-10 w-10 text-emerald-400 animate-spin" />
            <div className="text-sm font-semibold text-white">Käynnistetään kameraa…</div>
          </div>
        )}
        {/* Flash effect on capture */}
        {flashing && <div className="absolute inset-0 bg-white opacity-80 pointer-events-none" />}
        {/* Upload overlay */}
        {phase === "uploading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/80" data-testid="fast-capture-uploading">
            <Loader2 className="h-10 w-10 text-emerald-400 animate-spin" />
            <div className="font-display text-base font-semibold text-white">Ladataan kuvia…</div>
            <div className="text-sm text-white/70">{uploaded}/{items.length}</div>
          </div>
        )}
      </div>

      {/* Bottom: thumbnail strip + shutter + Valmis */}
      <div className="absolute bottom-0 inset-x-0 z-10 bg-gradient-to-t from-black/85 via-black/60 to-transparent pt-4 pb-4 px-4">
        {/* Thumbnail strip */}
        {items.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1" data-testid="fast-capture-thumbs">
            {items.map((it) => (
              <div key={it.id} className="relative flex-shrink-0 h-14 w-14 rounded-md overflow-hidden bg-white/10 border border-white/20" data-testid={`fast-capture-thumb-${it.id}`}>
                <img src={it.previewUrl} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeItem(it.id)}
                  className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-rose-600"
                  data-testid={`fast-capture-remove-${it.id}`}
                  aria-label="Poista kuva"
                >
                  <Trash2 className="h-2.5 w-2.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        {/* Action row: shutter + Valmis */}
        <div className="flex items-center justify-between gap-3">
          <div className="w-20 text-white/80 text-xs font-semibold">{items.length > 0 ? `${items.length} kuvaa` : ""}</div>
          <button
            type="button"
            onClick={shoot}
            disabled={phase !== "live"}
            className="h-20 w-20 rounded-full bg-white shadow-2xl active:scale-95 transition-transform flex items-center justify-center disabled:opacity-50"
            data-testid="fast-capture-shutter"
            aria-label="Ota kuva"
          >
            <div className="h-16 w-16 rounded-full bg-white border-4 border-slate-900 flex items-center justify-center">
              <Camera className="h-7 w-7 text-slate-900" />
            </div>
          </button>
          <button
            type="button"
            onClick={uploadAll}
            disabled={items.length === 0 || phase === "uploading"}
            className="w-20 h-12 rounded-xl bg-emerald-500 text-white text-sm font-bold disabled:opacity-40 disabled:bg-slate-600 transition-all flex flex-col items-center justify-center leading-tight"
            data-testid="fast-capture-finish"
          >
            <Check className="h-4 w-4" />
            <span className="text-[10px]">Valmis{items.length > 0 ? ` (${items.length})` : ""}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
